import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { productService } from "@/features/products/services/product-api.service";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { ProductType } from "@/shared/types/enums";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";

interface GownItem {
  productId: number;
  quantity: number;
}

interface GownItemsFieldProps {
  items: GownItem[];
  onChange: (items: GownItem[]) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 10;

function formatProductLabel(product: ProductResponseDTO): string {
  const parts = [product.brand, product.color];
  if (product.name) parts.push(product.name);
  return parts.join(" · ");
}

export const GownItemsField = memo(function GownItemsField({
  items,
  onChange,
}: GownItemsFieldProps) {
  const [productCache, setProductCache] = useState<
    Map<number, ProductResponseDTO>
  >(() => new Map());
  const lookedUpIdsRef = useRef<Set<number>>(new Set());

  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ProductResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftProduct, setDraftProduct] = useState<ProductResponseDTO | null>(
    null,
  );

  useEffect(() => {
    const missing = items
      .map((i) => i.productId)
      .filter((id) => !productCache.has(id) && !lookedUpIdsRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => lookedUpIdsRef.current.add(id));
    const controller = new AbortController();
    Promise.all(
      missing.map((id) =>
        productService
          .getByIdUser(id, { signal: controller.signal })
          .catch(() => null),
      ),
    ).then((products) => {
      if (controller.signal.aborted) return;
      const resolved = products.filter(
        (p): p is ProductResponseDTO => p !== null,
      );
      if (resolved.length === 0) return;
      setProductCache((prev) => {
        const next = new Map(prev);
        resolved.forEach((p) => next.set(p.id, p));
        return next;
      });
    });
    return () => controller.abort();
  }, [items, productCache]);

  const fetchProducts = useCallback(
    async (rawTerm: string, signal: AbortSignal) => {
      try {
        const response = await productService.searchUser(
          {
            type: [ProductType.GOWN],
            name: rawTerm.trim(),
            size: SEARCH_PAGE_SIZE,
          },
          { signal },
        );
        setShowResults(true);
        return response.content;
      } catch (error: unknown) {
        if (!signal.aborted) {
          toast.error(getApiErrorMessage(error, "Erro ao buscar produtos"));
        }
        throw error;
      }
    },
    [],
  );

  useDebouncedSearch({
    term,
    enabled: !draftProduct,
    fetcher: fetchProducts,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) => toast.error(getApiErrorMessage(e, "Erro ao buscar peças")),
    debounceMs: SEARCH_DEBOUNCE_MS,
  });

  const handleSelect = (product: ProductResponseDTO) => {
    setDraftProduct(product);
    setProductCache((prev) => {
      if (prev.has(product.id)) return prev;
      const next = new Map(prev);
      next.set(product.id, product);
      return next;
    });
    setTerm("");
    setShowResults(false);
  };

  const handleAdd = () => {
    if (!draftProduct || draftQuantity < 1) return;
    if (items.some((i) => i.productId === draftProduct.id)) {
      toast.error("Esse produto já foi adicionado");
      return;
    }
    onChange([
      ...items,
      { productId: draftProduct.id, quantity: draftQuantity },
    ]);
    setDraftProduct(null);
    setDraftQuantity(1);
  };

  const handleQuantityChange = (productId: number, quantity: number) => {
    onChange(
      items.map((i) => (i.productId === productId ? { ...i, quantity } : i)),
    );
  };

  const handleRemove = (productId: number) => {
    onChange(items.filter((i) => i.productId !== productId));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_auto] gap-2 items-end">
        <div className="space-y-1">
          <Label htmlFor="gown-product-search">Produto</Label>
          {draftProduct ? (
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
              <span className="flex-1 truncate">
                {formatProductLabel(draftProduct)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDraftProduct(null)}
              >
                Trocar
              </Button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="gown-product-search"
                placeholder="Busque pelo nome do produto"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="pl-8"
              />
              {searching ? (
                <Spinner className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4" />
              ) : null}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="gown-quantity">Quantidade</Label>
          <Input
            id="gown-quantity"
            type="number"
            min={1}
            value={draftQuantity}
            onChange={(e) => setDraftQuantity(Number(e.target.value) || 1)}
          />
        </div>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!draftProduct || draftQuantity < 1}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </Button>
      </div>

      {showResults && !draftProduct && results.length > 0 ? (
        <div className="rounded-md border border-input bg-background max-h-64 overflow-y-auto">
          {results.map((product) => (
            <button
              type="button"
              key={product.id}
              onClick={() => handleSelect(product)}
              className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border last:border-b-0 text-sm"
            >
              {formatProductLabel(product)}
            </button>
          ))}
        </div>
      ) : null}

      {showResults &&
      !draftProduct &&
      !searching &&
      results.length === 0 &&
      term.length >= 1 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum produto encontrado.
        </p>
      ) : null}

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum item adicionado. Você pode salvar o aluguel sem itens e
          completá-lo depois.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            const product = productCache.get(item.productId);
            return (
              <li
                key={item.productId}
                className="flex items-center gap-3 rounded-md border border-input bg-background p-3"
              >
                <span className="flex-1 text-sm truncate">
                  {product
                    ? formatProductLabel(product)
                    : `Produto #${item.productId}`}
                </span>
                <Input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) =>
                    handleQuantityChange(
                      item.productId,
                      Number(e.target.value) || 1,
                    )
                  }
                  className="w-20"
                  aria-label={`Quantidade do produto ${product?.name ?? item.productId}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleRemove(item.productId)}
                  aria-label="Remover item"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});
